import { Select, Table } from "antd";
import { ColumnProps } from "antd/lib/table";
import { END_USER_DETAIL_ROUTE } from "config/route-consts";
import React from "react";
import { Link } from "react-router-dom";
import {
  ActionLog,
  ActionObjectType,
  ActionSource,
  ACTION_OBJECT_ICON,
  ACTION_OBJECT_LABEL,
  ACTION_SOURCE_LABEL,
  DEMO_ACTION_LOGS,
  DEMO_CUSTOMERS,
  LogCustomer,
} from "./system-log-demo-data";
import {
  DateRange,
  formatDateTime,
  getDefaultRange,
  isDefaultRange,
  Initials,
  LogEmpty,
  LogPagination,
  PAGE_SIZE,
  RangeField,
  ResetFilterButton,
  SelectField,
} from "./SystemLogShared";

interface ActionFilter {
  customerId?: number;
  dormitoryId?: number;
  range: DateRange;
  objectType?: ActionObjectType;
  source?: ActionSource;
}

const TYPE_OPTIONS = (Object.keys(ACTION_OBJECT_LABEL) as ActionObjectType[]).map((value) => ({
  value,
  label: ACTION_OBJECT_LABEL[value],
}));
const SOURCE_OPTIONS = (Object.keys(ACTION_SOURCE_LABEL) as ActionSource[]).map((value) => ({
  value,
  label: ACTION_SOURCE_LABEL[value],
}));
// Ô chọn khách tìm được theo tên lẫn số điện thoại.
const CUSTOMER_OPTIONS = DEMO_CUSTOMERS.map((customer) => ({
  value: customer.id,
  label: `${customer.name} · ${customer.phone}`,
}));

const findCustomer = (id?: number) => DEMO_CUSTOMERS.find((customer) => customer.id === id);
const findDormitoryName = (log: ActionLog) =>
  findCustomer(log.customerId)?.dormitories.find((item) => item.id === log.dormitoryId)?.name;

function createFilter(): ActionFilter {
  return { range: getDefaultRange() };
}

// Lọc tại chỗ cho bản demo. Khi có API: gửi `filter` lên server, bỏ hàm này.
function filterDemo(list: ActionLog[], filter: ActionFilter) {
  return list.filter(
    (item) =>
      (!filter.customerId || item.customerId === filter.customerId) &&
      (!filter.dormitoryId || item.dormitoryId === filter.dormitoryId) &&
      (!filter.objectType || item.objectType === filter.objectType) &&
      (!filter.source || item.source === filter.source)
  );
}

function ActionCell({ item }: { item: ActionLog }) {
  return item.tone ? (
    <span className={`status-badge status-badge--${item.tone}`}>{item.action}</span>
  ) : (
    <>{item.action}</>
  );
}

function ObjectIcon({ type }: { type: ActionObjectType }) {
  return (
    <span className="system-log__obj-icon">
      <i className={ACTION_OBJECT_ICON[type]} />
    </span>
  );
}

function CustomerStrip({ customer }: { customer: LogCustomer }) {
  return (
    <div className="system-log__customer">
      <Initials name={customer.name} size="lg" />
      <div className="system-log__customer-text">
        <div className="system-log__customer-name">{customer.name}</div>
        <div className="system-log__cell-sub">
          {customer.phone} · {customer.email}
        </div>
      </div>
      <dl className="system-log__customer-facts">
        <div>
          <dt>Nhà</dt>
          <dd>{customer.dormitories.length}</dd>
        </div>
        <div>
          <dt>Home Controller</dt>
          <dd>{customer.homeControllerCount}</dd>
        </div>
        <div>
          <dt>Tác động 7 ngày</dt>
          <dd>{customer.actionCount7Days.toLocaleString("vi-VN")}</dd>
        </div>
      </dl>
      <Link className="system-log__customer-link" to={`${END_USER_DETAIL_ROUTE}?id=${customer.id}`}>
        Xem hồ sơ
        <i className="tio-chevron_right" />
      </Link>
    </div>
  );
}

export default function ActionLogTab() {
  const [filter, setFilter] = React.useState<ActionFilter>(createFilter);
  const [skip, setSkip] = React.useState(0);
  const [take, setTake] = React.useState(PAGE_SIZE);

  const customer = findCustomer(filter.customerId);
  const list = React.useMemo(() => filterDemo(DEMO_ACTION_LOGS, filter), [filter]);
  const page = list.slice(skip, skip + take);
  // Khách đang chọn không tính là "lọc": nút Xoá lọc chỉ gỡ các bộ lọc phụ.
  const isFiltered =
    !!filter.dormitoryId || !!filter.objectType || !!filter.source || !isDefaultRange(filter.range);

  const handleChangeFilter = React.useCallback(
    <K extends keyof ActionFilter>(key: K) =>
      (value: ActionFilter[K]) => {
        setFilter((current) => ({ ...current, [key]: value }));
        setSkip(0);
      },
    []
  );

  // Đổi khách thì bỏ lọc nhà của khách cũ.
  const handleChangeCustomer = React.useCallback((customerId?: number) => {
    setFilter((current) => ({ ...current, customerId, dormitoryId: undefined }));
    setSkip(0);
  }, []);

  const handlePagination = React.useCallback((nextSkip: number, nextTake: number) => {
    setSkip(nextSkip);
    setTake(nextTake);
  }, []);

  const handleResetFilter = React.useCallback(() => {
    setFilter((current) => ({ ...createFilter(), customerId: current.customerId }));
    setSkip(0);
  }, []);

  const columns: ColumnProps<ActionLog>[] = React.useMemo(
    () => [
      {
        title: "Thời gian",
        dataIndex: "createdAt",
        width: 128,
        render: (value: string) => (
          <span className="tabular-nums system-log__nowrap">{formatDateTime(value)}</span>
        ),
      },
      {
        title: "Đối tượng",
        dataIndex: "objectName",
        render: (_value: string, item) => (
          <div className="system-log__obj">
            <ObjectIcon type={item.objectType} />
            <div className="system-log__cell">
              <div className="system-log__cell-main system-log__truncate">{item.objectName}</div>
              <div className="system-log__cell-sub">
                {ACTION_OBJECT_LABEL[item.objectType]} · {item.roomName}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: "Tác động",
        dataIndex: "action",
        render: (_value: string, item) => <ActionCell item={item} />,
      },
      {
        title: "Nguồn",
        dataIndex: "source",
        render: (_value: string, item) => (
          <div className="system-log__cell">
            <div>{ACTION_SOURCE_LABEL[item.source]}</div>
            {item.actorName && <div className="system-log__cell-sub">{item.actorName}</div>}
          </div>
        ),
      },
      customer
        ? {
            title: "Nhà",
            key: "dormitory",
            render: (_value: string, item) => (
              <span className="text-muted-token">{findDormitoryName(item)}</span>
            ),
          }
        : {
            title: "Khách hàng · Nhà",
            key: "customer",
            render: (_value: string, item) => (
              <div className="system-log__cell">
                <div>{findCustomer(item.customerId)?.name}</div>
                <div className="system-log__cell-sub">{findDormitoryName(item)}</div>
              </div>
            ),
          },
    ],
    [customer]
  );

  return (
    <>
      <div className="system-log__toolbar">
        <div className="system-log__customer-field">
          <i className="tio-user_outlined" />
          <Select
            className="system-log__select"
            showSearch
            allowClear
            placeholder="Tất cả khách hàng"
            optionFilterProp="label"
            value={filter.customerId}
            options={CUSTOMER_OPTIONS}
            onChange={(value) => handleChangeCustomer(value as number | undefined)}
          />
        </div>
        {customer && customer.dormitories.length > 1 && (
          <SelectField
            placeholder={`Mọi nhà (${customer.dormitories.length})`}
            value={filter.dormitoryId}
            options={customer.dormitories.map((item) => ({ value: item.id, label: item.name }))}
            onChange={handleChangeFilter("dormitoryId")}
          />
        )}
        <RangeField value={filter.range} onChange={handleChangeFilter("range")} />
        <SelectField
          placeholder="Mọi loại"
          value={filter.objectType}
          options={TYPE_OPTIONS}
          onChange={handleChangeFilter("objectType")}
        />
        <SelectField
          placeholder="Mọi nguồn"
          value={filter.source}
          options={SOURCE_OPTIONS}
          onChange={handleChangeFilter("source")}
        />
        <ResetFilterButton visible={isFiltered} onClick={handleResetFilter} />
      </div>

      {customer && <CustomerStrip customer={customer} />}

      {list.length === 0 ? (
        <LogEmpty
          title={
            customer
              ? `${customer.name} chưa có tác động nào trong khoảng thời gian này`
              : isFiltered
              ? "Không có tác động nào khớp bộ lọc"
              : "Không có tác động nào trong khoảng thời gian này"
          }
          onReset={handleResetFilter}
        />
      ) : (
        <>
          <Table
            className="system-log__table"
            rowKey="id"
            columns={columns}
            dataSource={page}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
          <ul className="system-log__rows">
            {page.map((item) => (
              <li key={item.id}>
                <ObjectIcon type={item.objectType} />
                <div className="system-log__grow">
                  <div className="system-log__rows-top">
                    <span className="system-log__cell-main system-log__truncate">{item.objectName}</span>
                    <span className="system-log__cell-sub tabular-nums">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </div>
                  <div>
                    <ActionCell item={item} />{" "}
                    <span className="system-log__cell-sub">
                      · {ACTION_SOURCE_LABEL[item.source]}
                      {item.actorName && ` · ${item.actorName}`}
                    </span>
                  </div>
                  <div className="system-log__cell-sub">
                    {ACTION_OBJECT_LABEL[item.objectType]} · {item.roomName}
                    {!customer && ` · ${findCustomer(item.customerId)?.name}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <LogPagination
            skip={skip}
            take={take}
            total={list.length}
            unit="tác động"
            onChange={handlePagination}
          />
        </>
      )}
    </>
  );
}
